import type { CheckedState } from '@radix-ui/react-checkbox';
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import { classNames } from '~/utils/styles';

export interface SelectOptionProps<TItem> {
  /** The item to display */
  item: TItem;
  /** The style of the item for the virtualised list */
  style?: CSSProperties;
  /** Optional class name */
  className?: string;
  /** Sets the whether the item has been selected */
  isSelected?: CheckedState;
  /** Sets the whether the item is disabled */
  disabled?: boolean;
  //** The child component that get rendered */
  children?: ReactNode;
  /** Gets called when the item is clicked */
  onItemClick: (item: TItem) => void;
}

export function SelectOption<TItem>({
  item,
  children,
  style,
  className,
  isSelected,
  disabled,
  onItemClick,
}: SelectOptionProps<TItem>) {
  function handleOnKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (disabled) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      onItemClick(item);
    }
  }

  function handleItemClick() {
    if (disabled) {
      return;
    }
    onItemClick(item);
  }

  const classes = classNames(
    'body-200-light flex w-auto min-w-full items-center px-4 text-neutral-1000',
    'hover:cursor-pointer hover:bg-neutral-200 focus-visible:bg-neutral-200 focus-visible:outline-hidden',
    {
      'cursor-not-allowed! text-neutral-400 hover:bg-transparent': disabled,
      'bg-teal-300 hover:bg-teal-300': isSelected,
    },
    className,
  );

  const modifiedStyle = style
    ? {
        ...style,
        minWidth: '100%',
        width: 'auto',
      }
    : undefined;

  function ariaSelected() {
    if (isSelected === 'indeterminate') {
      return true;
    }
    return isSelected;
  }

  return (
    <div
      aria-selected={ariaSelected()}
      className={classes}
      onClick={handleItemClick}
      onKeyDown={(evt) => handleOnKeyDown(evt)}
      role="option"
      style={modifiedStyle}
      tabIndex={0}
    >
      {children}
    </div>
  );
}
